import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Rocket, ArrowLeft, DollarSign, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeployContract = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [totalSupply, setTotalSupply] = useState('');
  const [gasEstimate, setGasEstimate] = useState(null);
  const [deployedContracts, setDeployedContracts] = useState([]);

  useEffect(() => {
    fetchDeployedContracts();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDeployedContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts/my-contracts`, {
        headers: getAuthHeaders()
      });
      setDeployedContracts(response.data.contracts);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const handleEstimate = async () => {
    if (!tokenName || !tokenSymbol || !totalSupply) {
      toast.error('Please fill all fields');
      return;
    }

    setEstimating(true);
    try {
      const response = await axios.post(
        `${API}/contracts/estimate-deployment`,
        {
          token_name: tokenName,
          token_symbol: tokenSymbol,
          total_supply: parseInt(totalSupply)
        },
        { headers: getAuthHeaders() }
      );
      setGasEstimate(response.data);
      toast.success('Cost estimated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Estimation failed');
    } finally {
      setEstimating(false);
    }
  };

  const handleDeploy = async (e) => {
    e.preventDefault();
    
    if (!tokenName || !tokenSymbol || !totalSupply) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/contracts/deploy-token`,
        {
          token_name: tokenName,
          token_symbol: tokenSymbol,
          total_supply: parseInt(totalSupply)
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Token deployed successfully!');
      
      // Reset form
      setTokenName('');
      setTokenSymbol('');
      setTotalSupply('');
      setGasEstimate(null);
      
      // Refresh deployed contracts
      fetchDeployedContracts();
      
      // Show deployment details
      toast.success(
        `Contract Address: ${response.data.contract_address.substring(0, 10)}...`,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deployment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <h1 className="text-2xl font-bold">Deploy Contract</h1>
          </div>
          <span className="text-sm text-muted-foreground">{user?.email}</span>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Deploy Form */}
          <div>
            <Card className="glass border-border" data-testid="deploy-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-[#3b82f6]" />
                  Deploy ERC20 Token
                </CardTitle>
                <CardDescription>
                  Create your own token on Ethereum Mainnet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleDeploy} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tokenName">Token Name</Label>
                    <Input
                      id="tokenName"
                      type="text"
                      placeholder="My Awesome Token"
                      value={tokenName}
                      onChange={(e) => setTokenName(e.target.value)}
                      required
                      data-testid="token-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tokenSymbol">Token Symbol</Label>
                    <Input
                      id="tokenSymbol"
                      type="text"
                      placeholder="MAT"
                      maxLength={5}
                      value={tokenSymbol}
                      onChange={(e) => setTokenSymbol(e.target.value.toUpperCase())}
                      required
                      data-testid="token-symbol-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="totalSupply">Total Supply</Label>
                    <Input
                      id="totalSupply"
                      type="number"
                      placeholder="1000000"
                      value={totalSupply}
                      onChange={(e) => setTotalSupply(e.target.value)}
                      required
                      data-testid="total-supply-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of tokens to create (18 decimals)
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleEstimate}
                      disabled={estimating || loading}
                      className="flex-1"
                      data-testid="estimate-btn"
                    >
                      {estimating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Estimating...
                        </>
                      ) : (
                        <>
                          <DollarSign className="h-4 w-4 mr-2" />
                          Estimate Cost
                        </>
                      )}
                    </Button>

                    <Button
                      type="submit"
                      disabled={loading || estimating}
                      className="flex-1 bg-[#3b82f6] hover:bg-[#2563eb]"
                      data-testid="deploy-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Deploying...
                        </>
                      ) : (
                        <>
                          <Rocket className="h-4 w-4 mr-2" />
                          Deploy Token
                        </>
                      )}
                    </Button>
                  </div>
                </form>

                {gasEstimate && (
                  <div className="mt-6 p-4 bg-secondary rounded-lg" data-testid="gas-estimate">
                    <h3 className="font-semibold mb-2">Deployment Cost Estimate</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Gas Estimate:</span>
                        <span>{gasEstimate.gas_estimate.toLocaleString()} units</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Gas Price:</span>
                        <span>{gasEstimate.gas_price_gwei.toFixed(2)} Gwei</span>
                      </div>
                      <div className="flex justify-between font-bold text-[#10b981]">
                        <span>Total Cost:</span>
                        <span>{gasEstimate.total_cost_eth.toFixed(6)} ETH</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="glass border-border mt-6">
              <CardHeader>
                <CardTitle>How It Works</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>1. <strong>Enter Details:</strong> Choose your token name, symbol, and supply</p>
                <p>2. <strong>Estimate Cost:</strong> See how much gas the deployment will cost</p>
                <p>3. <strong>Deploy:</strong> Click deploy and your token will be created on Ethereum</p>
                <p>4. <strong>Share:</strong> Your token address will be ready to use immediately</p>
              </CardContent>
            </Card>
          </div>

          {/* Deployed Contracts */}
          <div>
            <Card className="glass border-border" data-testid="contracts-list">
              <CardHeader>
                <CardTitle>Your Deployed Tokens</CardTitle>
                <CardDescription>
                  {deployedContracts.length} contract{deployedContracts.length !== 1 ? 's' : ''} deployed
                </CardDescription>
              </CardHeader>
              <CardContent>
                {deployedContracts.length > 0 ? (
                  <div className="space-y-4">
                    {deployedContracts.map((contract, index) => (
                      <div 
                        key={contract.contract_address || index}
                        className="p-4 bg-secondary rounded-lg space-y-2"
                        data-testid="contract-item"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold">{contract.token_name}</h3>
                            <p className="text-sm text-muted-foreground">{contract.token_symbol}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-[#10b981]">
                              {contract.total_supply.toLocaleString()} tokens
                            </p>
                          </div>
                        </div>
                        <div className="text-xs mono text-muted-foreground">
                          <div className="flex justify-between">
                            <span>Address:</span>
                            <span>{contract.contract_address?.substring(0, 20)}...</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Block:</span>
                            <span>#{contract.block_number}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Gas Used:</span>
                            <span>{contract.gas_used?.toLocaleString()}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => window.open(`https://etherscan.io/address/${contract.contract_address}`, '_blank')}
                        >
                          View on Etherscan
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Rocket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No contracts deployed yet</p>
                    <p className="text-sm mt-2">Deploy your first token to get started!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeployContract;
